// The click-to-select side panel: producer dial editing, control-node link
// editing, and read-only info for consumers/storage. Rebuilds its DOM only
// when the selected node changes (not every render frame) so in-progress
// edits (e.g. a focused dial input) never get clobbered by the game loop.

import { CONFIG, CONSUMER_TYPES, type ProducerType, PRODUCER_TYPES, STORAGE_TYPES } from "./config";
import { isDebugPanelOpen } from "./debug";
import { directionLabel, parseHexKey } from "./hexgrid";
import { computeControlNodeGroups, type SolveResult, weatherMultiplierFor } from "./solver";
import { type ConsumerNode, type GameState, type GridLine, type ProducerNode, phaseIndexOf } from "./state";
import type { WeatherDay } from "./weather";

export interface InspectorCallbacks {
  onDialChange: (nodeId: string, phase: number, value: number) => void;
  onJoinSelected: (hexId: string, lineIds: string[]) => void;
  onSplitSelected: (hexId: string, lineIds: string[]) => void;
  onSetStorageMode: (nodeId: string, mode: "auto" | "charge" | "discharge") => void;
  onDebugSetCapacity: (nodeId: string, capacityMw: number) => void;
  onToggleDismantle: (hexId: string) => void;
  onClose: () => void;
}

const GROUP_COLORS = ["#f87171", "#60a5fa", "#4ade80", "#facc15", "#c084fc", "#38bdf8"];

/** A producer's `effectivePct` is a single live value, not one per phase —
 * there's only one "now". To still show something useful on all
 * CONFIG.phasesPerDay dial rows, every row other than the current phase's
 * shows a *projection*: a chained simulation of the same per-phase ramp
 * rule the game itself applies in simulation.ts's advanceTurn, walking
 * forward from the real live value through each intervening phase's
 * target. It's not a guarantee — editing any target before then changes
 * it — hence "in N phases: ~XX%" rather than "now: XX%" for those rows. */
function projectEffectivePct(
  dial: number[],
  effectivePct: number,
  maxDialRampPerPhase: number,
  currentPhase: number,
  phasesPerDay: number,
): number[] {
  const result = new Array<number>(phasesPerDay);
  let running = effectivePct;
  result[currentPhase] = running;
  for (let step = 1; step < phasesPerDay; step++) {
    const phase = (currentPhase + step) % phasesPerDay;
    const target = dial[phase];
    const delta = Math.max(-maxDialRampPerPhase, Math.min(maxDialRampPerPhase, target - running));
    running += delta;
    result[phase] = running;
  }
  return result;
}

/** `projectEffectivePct`'s output is the raw dial trajectory (0-100, what
 * the ramp mechanic alone produces) — this scales it by the matching
 * weather multiplier per phase so the displayed number reflects actual
 * expected output, not just the un-weathered dial chase. A step that falls
 * within today (hasn't wrapped past Night→Morning yet) uses today's
 * weather; a step that wraps into tomorrow uses tomorrow's forecast —
 * that's the furthest ahead the game knows weather for, which is exactly
 * as far as one full phasesPerDay-length projection can reach. */
function applyWeatherToProjection(
  raw: number[],
  currentPhase: number,
  phasesPerDay: number,
  typeId: string,
  weatherToday: WeatherDay,
  weatherTomorrow: WeatherDay,
): number[] {
  const result = new Array<number>(phasesPerDay);
  for (let step = 0; step < phasesPerDay; step++) {
    const phase = (currentPhase + step) % phasesPerDay;
    const isTomorrow = currentPhase + step >= phasesPerDay;
    const mult = weatherMultiplierFor(isTomorrow ? weatherTomorrow : weatherToday, typeId);
    result[phase] = raw[phase] * mult;
  }
  return result;
}

function effectiveLabelText(phase: number, currentPhase: number, phasesPerDay: number, value: number): string {
  const stepsAway = (phase - currentPhase + phasesPerDay) % phasesPerDay;
  if (stepsAway === 0) return `now: ${Math.round(value)}%`;
  return `in ${stepsAway} phase${stepsAway > 1 ? "s" : ""}: ~${Math.round(value)}%`;
}

/** "dial" mode only: flags when the ramp hasn't (or won't have, by that
 * projected phase) caught up to what the player actually asked for —
 * compared against the *raw*, un-weathered projection so a cloudy/stormy
 * day's inherent scaling doesn't itself read as a mismatch (nearly every
 * phase would trip it otherwise, making the signal meaningless). Not
 * applicable to "switch" (only ever two exact target values, and reaches
 * them once the phase begins) or "fixed" (no target the player sets). */
function isDialMismatch(type: ProducerType, dialTarget: number, rawProjectedValue: number): boolean {
  return type.controlMode === "dial" && Math.round(rawProjectedValue) !== dialTarget;
}

function producerNoteText(type: ProducerType): string {
  const verb = type.controlMode === "switch" ? "Toggling" : "Editing";
  if (type.maxDialRampPerPhase >= 100) {
    return `${verb} the current phase applies immediately; a future phase applies once it begins.`;
  }
  return `${verb} the current phase nudges the live value immediately, capped at ${type.maxDialRampPerPhase} pts from where this phase started (plant inertia). A future phase applies once it begins.`;
}

/** Plain "X MW" text normally; while the debug panel is open, an editable
 * number input wired to debugSetNodeCapacity instead — debug mode is the
 * only time a node's nominal capacity can be changed after it's placed. */
function buildCapacityWidget(node: ProducerNode | ConsumerNode, callbacks: InspectorCallbacks): HTMLElement {
  if (!isDebugPanelOpen()) {
    const span = document.createElement("span");
    span.className = "dim capacity-display";
    span.textContent = `${Math.round(node.capacityMw)} MW`;
    return span;
  }
  const input = document.createElement("input");
  input.type = "number";
  input.min = "0";
  input.value = String(Math.round(node.capacityMw));
  input.className = "debug-input capacity-debug-input";
  input.title = "Debug: nominal capacity (MW)";
  input.addEventListener("input", () => {
    const value = Number(input.value);
    if (Number.isFinite(value)) callbacks.onDebugSetCapacity(node.id, value);
  });
  return input;
}

function buildDialInput(node: ProducerNode, phase: number, callbacks: InspectorCallbacks): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "number";
  input.min = "0";
  input.max = "100";
  input.value = String(node.dial[phase]);
  input.className = "dial-input";
  // "input" (not just "change") so the spinner arrows commit immediately —
  // some browsers are inconsistent about firing "change" for a
  // stepper-button click without a preceding focus/blur, and the render
  // loop's per-frame refreshDom will otherwise snap the box back to the
  // stale value on the very next frame if that click never reached state.
  input.addEventListener("input", () => {
    const value = Number(input.value);
    callbacks.onDialChange(node.id, phase, Number.isFinite(value) ? value : 0);
  });
  return input;
}

/** "On" targets this type's normal pattern value for the phase; "off"
 * targets 0. Whether a phase currently reads as on/off is derived from
 * `dial[phase] > 0` rather than a separate stored flag — dial is only ever
 * set to one of those two values for a "switch" plant, so that's a safe
 * (and one-less-thing-to-desync) proxy for it. */
function buildSwitchButton(node: ProducerNode, type: ProducerType, phase: number, callbacks: InspectorCallbacks): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = "dial-toggle-btn";
  const syncLabel = () => {
    const isOn = node.dial[phase] > 0;
    btn.textContent = isOn ? "On" : "Off";
    btn.classList.toggle("on", isOn);
  };
  syncLabel();
  btn.addEventListener("click", () => {
    const turningOn = !(node.dial[phase] > 0);
    callbacks.onDialChange(node.id, phase, turningOn ? type.defaultDialByPhase[phase] : 0);
    syncLabel();
  });
  return btn;
}

const inspectorEl = document.getElementById("inspector") as HTMLDivElement;

// Either a node id or a line id — mutually exclusive, see renderInspector.
let currentEntityId: string | null = null;
let selectedLineIds = new Set<string>();
let dialWidgets: HTMLElement[] = [];
let activeCallbacks: InspectorCallbacks | null = null;

// Control-node chip DOM is kept alive across the render loop's ~60fps
// refreshes instead of being torn down and rebuilt every frame — see the
// big comment on refreshControlNodeChips for why that used to eat clicks.
let chipElements = new Map<string, HTMLButtonElement>();
let emptyMessageEl: HTMLDivElement | null = null;
let joinBtnEl: HTMLButtonElement | null = null;
let splitBtnEl: HTMLButtonElement | null = null;

export function closeInspector(): void {
  currentEntityId = null;
  selectedLineIds = new Set();
  chipElements = new Map();
  emptyMessageEl = null;
  joinBtnEl = null;
  splitBtnEl = null;
  activeCallbacks = null;
  inspectorEl.classList.add("hidden");
  inspectorEl.innerHTML = "";
}

export function renderInspector(
  state: GameState,
  selectedNodeId: string | null,
  selectedLineId: string | null,
  preview: SolveResult,
  callbacks: InspectorCallbacks,
): void {
  const node = selectedNodeId ? state.nodes[selectedNodeId] : null;
  const line = !node && selectedLineId ? state.lines[selectedLineId] : null;
  if (!node && !line) {
    closeInspector();
    return;
  }
  inspectorEl.classList.remove("hidden");
  activeCallbacks = callbacks;

  const entityId = node ? node.id : line!.id;
  if (currentEntityId !== entityId) {
    currentEntityId = entityId;
    selectedLineIds = new Set();
    chipElements = new Map();
    emptyMessageEl = null;
    joinBtnEl = null;
    splitBtnEl = null;
    dialWidgets = [];
    if (node) buildDom(state, node, callbacks);
    else buildLineDom(line!, preview);
    return;
  }

  if (node) refreshDom(state, node);
  else refreshLineDom(line!, preview);
}

function buildDom(state: GameState, node: GameState["nodes"][string], callbacks: InspectorCallbacks): void {
  inspectorEl.innerHTML = "";

  const header = document.createElement("div");
  header.className = "inspector-header";
  const closeBtn = document.createElement("button");
  closeBtn.textContent = "✕";
  closeBtn.className = "inspector-close";
  closeBtn.addEventListener("click", () => callbacks.onClose());

  if (node.kind === "producer") {
    const type = PRODUCER_TYPES[node.typeId];
    const nameEl = document.createElement("strong");
    nameEl.textContent = type.label;
    header.append(nameEl, buildCapacityWidget(node, callbacks));
    header.appendChild(closeBtn);
    inspectorEl.appendChild(header);

    if (type.controlMode === "fixed") {
      const pattern = document.createElement("div");
      pattern.className = "dim";
      pattern.textContent = `Not player-controlled — always follows its own pattern (adjusted by weather): ${type.defaultDialByPhase.map((v) => v + "%").join(" / ")}`;
      inspectorEl.appendChild(pattern);
      dialWidgets = [];
      return;
    }

    const note = document.createElement("div");
    note.className = "dim";
    note.textContent = producerNoteText(type);
    inspectorEl.appendChild(note);

    const currentPhase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
    const rawProjected = projectEffectivePct(
      node.dial,
      node.effectivePct,
      type.maxDialRampPerPhase,
      currentPhase,
      CONFIG.phasesPerDay,
    );
    const displayProjected = applyWeatherToProjection(
      rawProjected,
      currentPhase,
      CONFIG.phasesPerDay,
      type.id,
      state.weatherToday,
      state.weatherTomorrow,
    );

    dialWidgets = [];
    for (let phase = 0; phase < CONFIG.phasesPerDay; phase++) {
      const row = document.createElement("div");
      row.className = "dial-row";
      const label = document.createElement("span");
      label.className = "dial-label";
      label.textContent = CONFIG.phaseNames[phase];
      label.classList.toggle("current-phase", phase === currentPhase);

      const widget = type.controlMode === "switch" ? buildSwitchButton(node, type, phase, callbacks) : buildDialInput(node, phase, callbacks);

      const effective = document.createElement("span");
      effective.className = "dim dial-effective";
      effective.textContent = effectiveLabelText(phase, currentPhase, CONFIG.phasesPerDay, displayProjected[phase]);
      effective.classList.toggle("dial-mismatch", isDialMismatch(type, node.dial[phase], rawProjected[phase]));
      row.append(label, widget, effective);
      dialWidgets.push(widget);
      inspectorEl.appendChild(row);
    }
  } else if (node.kind === "consumer") {
    const type = CONSUMER_TYPES[node.typeId];
    const nameEl = document.createElement("strong");
    nameEl.textContent = type.label;
    header.append(nameEl, buildCapacityWidget(node, callbacks));
    header.appendChild(closeBtn);
    inspectorEl.appendChild(header);
    const pattern = document.createElement("div");
    pattern.className = "dim";
    pattern.textContent = `Demand by phase: ${type.demandPattern.map((f) => Math.round(f * 100) + "%").join(" / ")}`;
    inspectorEl.appendChild(pattern);
  } else if (node.kind === "storage") {
    const type = STORAGE_TYPES[node.source];
    header.innerHTML = `<strong>${type.label}</strong> <span class="dim">${node.rateMw} MW rate</span>`;
    header.appendChild(closeBtn);
    inspectorEl.appendChild(header);

    const charge = document.createElement("div");
    charge.className = "storage-charge";
    inspectorEl.appendChild(charge);

    const modeNote = document.createElement("div");
    modeNote.className = "dim";
    modeNote.textContent = "Auto: the grid decides. Charge/Discharge: always does that, even against local need.";
    inspectorEl.appendChild(modeNote);

    const modeRow = document.createElement("div");
    modeRow.className = "storage-mode-row";
    (["auto", "charge", "discharge"] as const).forEach((mode) => {
      const btn = document.createElement("button");
      btn.className = "mode-btn storage-mode-btn";
      btn.dataset.mode = mode;
      btn.textContent = mode[0].toUpperCase() + mode.slice(1);
      btn.classList.toggle("selected", node.mode === mode);
      btn.addEventListener("click", () => callbacks.onSetStorageMode(node.id, mode));
      modeRow.appendChild(btn);
    });
    inspectorEl.appendChild(modeRow);

    if (node.source === "battery") {
      const btn = document.createElement("button");
      btn.className = "dismantle-btn";
      inspectorEl.appendChild(btn);
      btn.addEventListener("click", () => callbacks.onToggleDismantle(node.id));
    }
  } else if (node.kind === "control") {
    header.innerHTML = `<strong>Control Node</strong>`;
    header.appendChild(closeBtn);
    inspectorEl.appendChild(header);

    const help = document.createElement("div");
    help.className = "dim";
    help.textContent = "Select lines below (same color = joined), then Join or Split them off.";
    inspectorEl.appendChild(help);

    const chipsContainer = document.createElement("div");
    chipsContainer.className = "link-chips";
    chipsContainer.id = "inspector-link-chips";
    inspectorEl.appendChild(chipsContainer);

    const actionsRow = document.createElement("div");
    actionsRow.className = "link-actions";
    actionsRow.id = "inspector-link-actions";
    inspectorEl.appendChild(actionsRow);

    const joinBtn = document.createElement("button");
    joinBtn.className = "link-action-btn";
    joinBtn.textContent = "Join selected";
    joinBtn.disabled = true;
    joinBtn.addEventListener("click", () => {
      callbacks.onJoinSelected(node.id, [...selectedLineIds]);
      selectedLineIds = new Set();
      refreshControlNodeChips(state, node.id, chipsContainer, callbacks);
    });
    actionsRow.appendChild(joinBtn);
    joinBtnEl = joinBtn;

    const splitBtn = document.createElement("button");
    splitBtn.className = "link-action-btn";
    splitBtn.textContent = "Split off selected";
    splitBtn.disabled = true;
    splitBtn.addEventListener("click", () => {
      callbacks.onSplitSelected(node.id, [...selectedLineIds]);
      selectedLineIds = new Set();
      refreshControlNodeChips(state, node.id, chipsContainer, callbacks);
    });
    actionsRow.appendChild(splitBtn);
    splitBtnEl = splitBtn;

    const btn = document.createElement("button");
    btn.className = "dismantle-btn";
    inspectorEl.appendChild(btn);
    btn.addEventListener("click", () => callbacks.onToggleDismantle(node.id));

    refreshControlNodeChips(state, node.id, chipsContainer, callbacks);
  }
}

/** Patches the control-node chip list in place instead of tearing it down
 * and rebuilding it — this runs every render-loop frame (~60/s) via
 * refreshDom, and a full `container.innerHTML = ""` rebuild on that cadence
 * used to destroy and recreate every chip/button element between a click's
 * mousedown and mouseup, which made the browser drop the click entirely
 * (no "click" event fires when the pressed element gets removed from the
 * DOM before release). Chips are now only added/removed when the actual set
 * of touching lines changes; everything else is an attribute/class update
 * on the same persistent elements. */
function refreshControlNodeChips(
  state: GameState,
  hexId: string,
  container: HTMLDivElement,
  callbacks: InspectorCallbacks,
): void {
  const groups = computeControlNodeGroups(state, hexId);
  const groupColorIndex = new Map<string, number>();
  let nextColor = 0;
  for (const root of groups.values()) {
    if (!groupColorIndex.has(root)) groupColorIndex.set(root, nextColor++);
  }

  const activeLines = Object.values(state.lines).filter((l) => l.status === "active");
  const touching = activeLines.filter((l) => l.a === hexId || l.b === hexId);
  const touchingIds = new Set(touching.map((l) => l.id));

  // A line that stopped touching this node (dismantled/broken) can't stay
  // selected, and its chip must go away.
  for (const id of selectedLineIds) {
    if (!touchingIds.has(id)) selectedLineIds.delete(id);
  }
  for (const [id, chip] of chipElements) {
    if (!touchingIds.has(id)) {
      chip.remove();
      chipElements.delete(id);
    }
  }

  if (touching.length === 0) {
    if (!emptyMessageEl) {
      emptyMessageEl = document.createElement("div");
      emptyMessageEl.className = "dim";
      emptyMessageEl.textContent = "No lines connected yet.";
      container.appendChild(emptyMessageEl);
    }
  } else if (emptyMessageEl) {
    emptyMessageEl.remove();
    emptyMessageEl = null;
  }

  for (const line of touching) {
    let chip = chipElements.get(line.id);
    if (!chip) {
      chip = document.createElement("button");
      chip.className = "link-chip";
      const otherHex = line.a === hexId ? line.b : line.a;
      const other = parseHexKey(otherHex);
      chip.textContent = `→ ${directionLabel(parseHexKey(hexId), other)}`;
      chip.addEventListener("click", () => {
        if (selectedLineIds.has(line.id)) selectedLineIds.delete(line.id);
        else selectedLineIds.add(line.id);
        refreshControlNodeChips(state, hexId, container, callbacks);
      });
      chipElements.set(line.id, chip);
      container.appendChild(chip);
    }

    const groupRoot = groups.get(line.id)!;
    const color = GROUP_COLORS[(groupColorIndex.get(groupRoot) ?? 0) % GROUP_COLORS.length];
    chip.style.borderColor = color;
    chip.style.color = color;
    chip.classList.toggle("selected", selectedLineIds.has(line.id));
  }

  if (joinBtnEl) joinBtnEl.disabled = selectedLineIds.size < 2;
  if (splitBtnEl) splitBtnEl.disabled = selectedLineIds.size < 1 || selectedLineIds.size === touching.length;
}

/** Guarded the same way the dial inputs are — skip while the debug capacity
 * input is focused, so an in-progress edit doesn't get clobbered by the
 * render loop reading it back before the edit finishes propagating. */
function refreshCapacityWidget(node: ProducerNode | ConsumerNode): void {
  const el = inspectorEl.querySelector<HTMLElement>(".capacity-display, .capacity-debug-input");
  if (!el) return;
  if (el instanceof HTMLInputElement) {
    if (document.activeElement !== el) el.value = String(Math.round(node.capacityMw));
  } else {
    el.textContent = `${Math.round(node.capacityMw)} MW`;
  }
}

function refreshDom(state: GameState, node: GameState["nodes"][string]): void {
  if (node.kind === "producer") {
    refreshCapacityWidget(node);
    const type = PRODUCER_TYPES[node.typeId];
    if (type.controlMode === "fixed") return; // no dial widgets exist — nothing else dynamic to refresh

    const currentPhase = phaseIndexOf(state.turn, CONFIG.phasesPerDay);
    const rawProjected = projectEffectivePct(
      node.dial,
      node.effectivePct,
      type.maxDialRampPerPhase,
      currentPhase,
      CONFIG.phasesPerDay,
    );
    const displayProjected = applyWeatherToProjection(
      rawProjected,
      currentPhase,
      CONFIG.phasesPerDay,
      type.id,
      state.weatherToday,
      state.weatherTomorrow,
    );
    dialWidgets.forEach((widget, phase) => {
      if (widget instanceof HTMLInputElement) {
        if (document.activeElement !== widget) widget.value = String(node.dial[phase]);
      } else if (widget instanceof HTMLButtonElement) {
        const isOn = node.dial[phase] > 0;
        widget.textContent = isOn ? "On" : "Off";
        widget.classList.toggle("on", isOn);
      }
      const label = widget.previousElementSibling as HTMLElement | null;
      label?.classList.toggle("current-phase", phase === currentPhase);
      const effective = widget.nextElementSibling as HTMLElement | null;
      if (effective) {
        effective.textContent = effectiveLabelText(phase, currentPhase, CONFIG.phasesPerDay, displayProjected[phase]);
        effective.classList.toggle("dial-mismatch", isDialMismatch(type, node.dial[phase], rawProjected[phase]));
      }
    });
  } else if (node.kind === "consumer") {
    refreshCapacityWidget(node);
  } else if (node.kind === "storage") {
    const chargeEl = inspectorEl.querySelector(".storage-charge");
    if (chargeEl) {
      const pct = Math.round((node.chargeMwh / node.capacityMwh) * 100);
      chargeEl.textContent = `Charge: ${Math.round(node.chargeMwh)} / ${node.capacityMwh} MWh (${pct}%)`;
    }
    inspectorEl.querySelectorAll<HTMLButtonElement>(".storage-mode-btn").forEach((btn) => {
      btn.classList.toggle("selected", btn.dataset.mode === node.mode);
    });
    if (node.source === "battery") {
      const btn = inspectorEl.querySelector(".dismantle-btn");
      if (btn) btn.textContent = dismantleButtonLabel(node.status, node.builtThisTurn);
    }
  } else if (node.kind === "control") {
    const btn = inspectorEl.querySelector(".dismantle-btn");
    if (btn) btn.textContent = dismantleButtonLabel(node.status, node.builtThisTurn);
    // Patched every refresh (see refreshControlNodeChips) so a
    // saturated/broken line disappears from the touching-line list live,
    // without tearing down the interactive chips/buttons every frame.
    const chipsContainer = document.getElementById("inspector-link-chips") as HTMLDivElement | null;
    if (chipsContainer && activeCallbacks) {
      refreshControlNodeChips(state, node.id, chipsContainer, activeCallbacks);
    }
  }
}

function dismantleButtonLabel(status: "active" | "dismantling", builtThisTurn: boolean): string {
  if (status === "active" && builtThisTurn) return "Cancel (free)";
  if (status === "active") return "Dismantle";
  return "Cancel dismantle";
}

// --- line inspection (Inspect mode) — read-only: transported power and
// status, no dismantle action here (that's still Line mode's job; see
// doc/TODOS.md — clicking a line in Inspect mode used to toggle dismantle,
// which wasn't what "inspect" implies). ---

function buildLineDom(line: GridLine, preview: SolveResult): void {
  inspectorEl.innerHTML = "";

  const header = document.createElement("div");
  header.className = "inspector-header";
  header.innerHTML = `<strong>Line</strong> <span class="dim">${line.lengthKm} km</span>`;
  const closeBtn = document.createElement("button");
  closeBtn.textContent = "✕";
  closeBtn.className = "inspector-close";
  closeBtn.addEventListener("click", () => activeCallbacks?.onClose());
  header.appendChild(closeBtn);
  inspectorEl.appendChild(header);

  const info = document.createElement("div");
  info.className = "line-info";
  inspectorEl.appendChild(info);

  refreshLineDom(line, preview);
}

function refreshLineDom(line: GridLine, preview: SolveResult): void {
  const info = inspectorEl.querySelector(".line-info");
  if (!info) return;

  const flow = preview.lineFlowsMw[line.id] ?? 0;
  const magnitude = Math.abs(flow);
  const saturated = preview.saturatedLineIds.includes(line.id);
  const a = parseHexKey(line.a);
  const b = parseHexKey(line.b);
  const towardLabel = magnitude > 0.05 ? directionLabel(flow >= 0 ? a : b, flow >= 0 ? b : a) : null;

  const flowLine = towardLabel ? `${magnitude.toFixed(1)} MW → ${towardLabel}` : "No flow right now";
  const statusLine =
    line.status === "dismantling"
      ? `Dismantling — back online in ${line.dismantleTurnsLeft} turn${line.dismantleTurnsLeft === 1 ? "" : "s"} if cancelled`
      : "Active";

  info.innerHTML = `
    <div>${flowLine}</div>
    ${saturated ? '<div class="warning">⚠ Saturated — will break next turn</div>' : ""}
    <div class="dim">${statusLine}</div>
  `;
}
